import { AsyncPipe, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { FlexyooAlertsService } from '@flexyoo/common';
import { EmployeeCartDocumentsDataAccessService } from '@flexyoo/employee/data-access';
import type { EmployeeSelectedBenefit, IEmployeeCartArchiveItem } from '@flexyoo/employee/models';
import { employeeSelectedBenefitsSelectors } from '@flexyoo/employee/store';
import { findCartAddendum, resolveEmployeeCartSignTarget } from '@flexyoo/employee/util';
import { environment } from '@flexyoo/environment';
import { downloadURI } from '@flexyoo/shared/functions';
import { FilesDataAccessService } from '@flexyoo/shared/services';
import { tenantSettingsSelectors } from '@flexyoo/tenant/store';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, map } from 'rxjs';
import { EmployeeBenefitAgreementSignDigitalComponent } from 'src/app/employee/benefit-agreement-sign-digital/benefit-agreement-sign-digital.component';
import { BenefitAddendumSignComponent } from '../../benefit-addendum-sign/benefit-addendum-sign.component';

@Component({
  selector: 'flexyoo-employee-basket-cart-sign',
  templateUrl: './employee-basket-cart-sign.component.html',
  styleUrl: './employee-basket-cart-sign.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    UpperCasePipe,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    BenefitAddendumSignComponent,
    EmployeeBenefitAgreementSignDigitalComponent,
    MatDivider,
  ],
})
export class EmployeeBasketCartSignComponent {
  private readonly store = inject(Store);
  private readonly cartDocumentsService = inject(EmployeeCartDocumentsDataAccessService);
  private readonly filesDataAccessService = inject(FilesDataAccessService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly alerts = inject(FlexyooAlertsService);
  private readonly translate = inject(TranslateService);

  readonly targetCartId = input<number | null>(null);
  readonly cancelSign = output<void>();
  readonly refresh = output<void>();

  protected readonly showUploadSignBlock = environment.useUploadSignMethod;

  private readonly tenantSettings$ = this.store.select(
    tenantSettingsSelectors.selectTenantSettings
  );
  protected readonly showQuillSignatureType$ = this.tenantSettings$.pipe(
    map((settings) => settings?.useQuillBelgianEIDSignature || settings?.useQuillItsMeSignature),
    distinctUntilChanged()
  );
  protected readonly showManualSignatureType$ = this.tenantSettings$.pipe(
    map((settings) => settings?.useManualSignature),
    distinctUntilChanged()
  );
  protected readonly showQuillSignatureType = toSignal(this.showQuillSignatureType$, {
    initialValue: false,
  });

  private readonly currentCart = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentCartOrSimulatedDraft)
  );
  private readonly currentCartBenefits = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCurrentCartBenefits),
    { initialValue: [] as EmployeeSelectedBenefit[] }
  );
  private readonly cartHistories = toSignal(
    this.store.select(employeeSelectedBenefitsSelectors.selectCartHistories),
    { initialValue: null as IEmployeeCartArchiveItem[] | null }
  );

  private readonly resolvedTarget = computed(() =>
    resolveEmployeeCartSignTarget(
      this.targetCartId(),
      this.currentCart(),
      this.currentCartBenefits(),
      this.cartHistories()
    )
  );

  protected readonly cart = computed(() => this.resolvedTarget().cart);

  protected readonly benefits = computed(() => this.resolvedTarget().benefits);

  protected readonly cartAddendum = computed(() => findCartAddendum(this.cart()?.documents ?? []));

  protected readonly hasValidCartAddendum = computed(() => {
    const addendum = this.cartAddendum();
    return !!addendum && !addendum.DisapprovedReason;
  });

  protected readonly isInsufficientBudget = computed(() =>
    this.benefits().some((benefit) => benefit.hasInsufficientBudget)
  );

  protected onCancelSign(): void {
    this.cancelSign.emit();
  }

  protected onCartAddendumSignStatusChanged(isSigned: boolean): void {
    if (isSigned) {
      this.cancelSign.emit();
    }

    this.refresh.emit();
  }

  private downloadBlobResponse(
    response: { body: Blob | null; headers: { get(name: string): string | null } },
    fallbackFileName: string
  ): void {
    if (!response?.body) {
      return;
    }

    const url = window.URL.createObjectURL(response.body);
    try {
      downloadURI(
        url,
        response.headers.get('x-return-file') ??
          response.headers.get('x-file-name') ??
          fallbackFileName
      );
    } finally {
      window.URL.revokeObjectURL(url);
    }
  }

  protected onDownloadCartAddendum(): void {
    const appFileId = this.cartAddendum()?.AppFileId;
    if (!appFileId) {
      return;
    }

    this.filesDataAccessService
      .downloadFileById(appFileId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.downloadBlobResponse(response, 'cart-addendum.pdf');
      });
  }

  protected onDownloadCartAddendumTemplate(): void {
    const cartId = this.cart()?.id;
    if (!cartId) {
      return;
    }

    this.cartDocumentsService
      .generateAddendum(cartId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        this.downloadBlobResponse(response, 'cart-addendum.pdf');
      });
  }

  protected onUploadCartAddendum(file: File): void {
    const cartId = this.cart()?.id;
    if (!cartId || !file) {
      return;
    }

    this.cartDocumentsService
      .uploadAddendum(cartId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.cancelSign.emit();
          this.refresh.emit();
        },
        error: () => {
          this.alerts.addAlert({
            type: 'warn',
            text: this.translate.instant('errors.cart-addendum-upload-error'),
          });
        },
      });
  }
}
